/**
 * Where to place the face on a customiser shape.
 *
 * The eyes live on a sphere, and `radiusAtAngle` snaps them to the real outline
 * scaled by the local radius. That scaling places their CENTRE correctly, but the eye
 * has a size: the margin left in front of the edge is multiplied by the same factor, so
 * a narrow silhouette in its direction pushes it against the edge until the mask opens
 * outward. The capsule looked like a notch cut into the body on `capsule`, `triangle`,
 * `nuage` and `goutte`.
 *
 * This module solves the problem ONCE, at load time, and returns a table of offsets.
 * That choice is the heart of the fix, far more than the geometry that follows:
 *
 * Solved inside the render loop, the correction reacts to everything that moves at sixty
 * frames per second — gaze drift, the pointer, the expression mid-morph, the nearest edge
 * changing, the most constrained eye changing. Seven variants were written that way and
 * all produced a visible motion artefact: permanent trembling, a 26-unit direction jump
 * when the reference edge flipped, sudden enlargement when size entered the calculation.
 * The flaw was in none of their geometries; it was in solving per frame.
 *
 * The rest of the engine does not work that way: poses are DECLARED and it only
 * interpolates them with known curves. A tabulated offset fits that mould. It does not
 * move when gaze drifts or the pointer moves, and on a shape or expression change it
 * only goes from one table entry to another, on that morph's curve. Trembling becomes
 * impossible by construction, instead of being fought off: interpolating between two
 * constants is monotone, whereas re-solving the problem on an interpolating gaze is not.
 *
 * Pleasant corollary: the solver no longer has any continuity constraint, since it does
 * not run during animation. It can therefore probe a whole fan of directions and cover
 * the worst case of gaze drift, which a per-frame version could not afford.
 *
 * The table is a module constant, built at import from pure data: same nature as the
 * blink schedule in `face.ts`, deterministic and stateless, so it does not affect the
 * purity of `engine.sample(t)`.
 */

import { EXPRESSIONS, type BotExpression } from './expressions'
import { eyePoses } from './face'
import { eyeStyleForState, eyeSurfaceFit, gazeWithBias, isConcavePocketState, animationPocketRoll, pocketGazeBias } from './shapeEyes'
import { toPoints, type Point } from './shape'
import { SHAPES } from './skins'
import { STATES, type Pose, type StateDef, type StateId } from './states'

/** Reference radius for the solver. The rendered offset is in units of this radius. */
const R = 100

/**
 * Maximum amplitudes of idle liveliness, read from `liveliness`: `loopNoise` is
 * bounded to 1 in absolute value, so these sums are exact bounds, not estimates.
 *
 * They must be covered, otherwise the correction is right on the nominal pose and wrong
 * a second later: 7 degrees of roll move the eye by about a dozen units on a sphere of
 * radius 100. That is precisely what made `capsule` + `effraye` overflow when a
 * single-instant measurement declared it fine.
 */
const DERIVE_YAW = 5.5 + 1.6
const DERIVE_PITCH = 4.2 + 1.3
/** Centre drift, in sphere-radius units. */
const DERIVE_X = 0.006
const DERIVE_Y = 0.007

/** A pose's face — what the solver needs to place its capsules. */
interface Visage {
  gaze: Pose['gaze']
  split: number
  eyes: Pose['eyes']
}

/**
 * A capsule ready to be measured: its axis segment, and what is needed to compute the
 * radius to clear IN A GIVEN direction.
 *
 * A capsule is exactly a segment thickened by a disc of radius `r`. Its image under the
 * tangent matrix is therefore a segment thickened by an ELLIPSE, and the radius to clear
 * depends on direction: it is the support function of that ellipse, `r * |A^T u|`.
 *
 * Taking its largest singular value instead would be conservative but wrong in the only
 * direction that matters, and it is costly: the reference margin on the circle came out
 * NEGATIVE, so the requirement became toothless and 34 combinations still overflowed.
 */
interface Empreinte {
  /** centre, in viewBox units */
  x: number
  y: number
  /** half-axis vector */
  ax: number
  ay: number
  /** local disc radius, before transformation */
  r: number
  /** tangent-matrix columns, for the support function */
  m: [number, number, number, number]
}

/**
 * Imprints of both eyes of a face, placed on a profile.
 *
 * A capsule is exactly a segment thickened by a disc of radius `r`. Its image under the
 * tangent matrix is therefore a segment thickened by an ELLIPSE, and a disc of its major
 * axis radius covers it: hence the largest singular value. The measure stays conservative
 * in the strict sense — a positive margin guaranteeing the capsule fits inside.
 *
 * Blinking is not included: a closed eye does not need room made for it.
 */
function empreintes(
  def: StateDef,
  visage: Visage,
  sil: Pose['sil'],
  radii: number[]
): Empreinte[] {
  const out: Empreinte[] = []
  const poses = eyePoses(visage.gaze, R, visage.split)
  for (let i = 0; i < 2; i++) {
    const e = poses[i]!
    if (e.depth <= 0.02) continue
    const cfg = visage.eyes[i]!
    const style = eyeStyleForState(def, radii, cfg)
    const phi = (((cfg.tilt ?? 0) * style.tilt) * Math.PI) / 180
    const cp = Math.cos(phi)
    const sp = Math.sin(phi)
    const sw = style.w
    const sh = style.h
    const spread = style.spread
    const lift = style.lift * R
    const inset = style.inset
    const ax = e.a * cp + e.c * sp
    const ay = e.b * cp + e.d * sp
    const cx = -e.a * sp + e.c * cp
    const cy = -e.b * sp + e.d * cp

    const hw = Math.max(cfg.w * sw * R, 0.01) / 2
    const hh = Math.max(cfg.h * sh * R, 0.01) / 2
    const r = Math.min(hw, hh)
    // the axis is that of the larger dimension
    const long = hh > hw
    const demi = long ? hh - r : hw - r
    const px = e.x * inset
    const py = e.y * inset
    const { fitX, fitY } = eyeSurfaceFit(def, radii, px, py, sil.rot)
    out.push({
      x: px * fitX * spread,
      y: py * fitY + lift,
      ax: (long ? cx : ax) * demi,
      ay: (long ? cy : ay) * demi,
      r,
      m: [ax, ay, cx, cy]
    })
  }
  return out
}

/**
 * Shortest approach between a contour and a segment: the distance, and the vector from
 * the contour toward the segment — the direction that clears.
 *
 * Both come from the SAME pass. Computing them separately doubled the only real cost of
 * this module, which is this sweep.
 */
function approche(pts: Point[], x0: number, y0: number, x1: number, y1: number) {
  const sx = x1 - x0
  const sy = y1 - y0
  const len2 = sx * sx + sy * sy
  let best = Infinity
  let vx = 0
  let vy = 0
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i]!
    let t = len2 > 0 ? ((p.x - x0) * sx + (p.y - y0) * sy) / len2 : 0
    t = t < 0 ? 0 : t > 1 ? 1 : t
    const ex = x0 + t * sx - p.x
    const ey = y0 + t * sy - p.y
    const d2 = ex * ex + ey * ey
    if (d2 < best) {
      best = d2
      vx = ex
      vy = ey
    }
  }
  const d = Math.sqrt(best)
  return { d, ux: d > 1e-9 ? vx / d : 0, uy: d > 1e-9 ? vy / d : 0 }
}

/** A trial: capsules to fit inside a contour, and the reference contour. */
interface Epreuve {
  empreintes: Empreinte[]
  reference: Empreinte[]
  contour: Point[]
  calContour: Point[]
}

/**
 * Centre drift at rest, in viewBox units. It is added to the capsule radius: less than
 * one unit, so absorbing it this way costs less than multiplying trials by its four corners.
 */
const FLOTTEMENT = Math.hypot(DERIVE_X, DERIVE_Y) * R

/** Margin of the tightest capsule, and the direction that clears it. */
function pire(pts: Point[], emps: Empreinte[], tx: number, ty: number) {
  let marge = Infinity
  let ux = 0
  let uy = 0
  for (const e of emps) {
    const x = e.x + tx
    const y = e.y + ty
    const a = approche(pts, x - e.ax, y - e.ay, x + e.ax, y + e.ay)
    // ellipse support function in the approach direction
    const [m0, m1, m2, m3] = e.m
    const rayon =
      e.r * Math.hypot(m0 * a.ux + m1 * a.uy, m2 * a.ux + m3 * a.uy) + FLOTTEMENT
    if (a.d - rayon < marge) {
      marge = a.d - rayon
      ux = a.ux
      uy = a.uy
    }
  }
  return { marge, ux, uy }
}

/**
 * Probed directions and bisection steps. Their product is the cost of building the table,
 * the only figure to watch here.
 */
const DIRECTIONS = 12
const DICHOTOMIE = 8

/**
 * The offset to apply to both eyes for this shape, this state and this expression.
 *
 * A common TRANSLATION for both eyes, therefore an isometry: inter-eye spacing, sizes and
 * tilts are preserved to the pixel. The face is only placed a little lower on a body with
 * no room at the top — the gesture one would make by hand. Variants that bounded each eye
 * separately pulled the pair apart, and those that scaled the face shrank the eyes —
 * visibly.
 *
 * The target margin is that of the ORIGINAL profile, not strict clearance: on the circle
 * the outer eye already grazes the edge, 17.3 units on a sphere of radius 100, and that is
 * intentional — it gives volume. It is capped by what the shape offers at its centre,
 * otherwise the requirement is untenable on a flat body.
 *
 * DIRECTIONAL SEARCH, not descent. We seek the translation of smallest norm that fits, so
 * we probe a ring of directions and bisect the distance along each. Gradient descent was
 * written first and it does not converge: clearing the pair from one edge brings them
 * closer to the other, so it gropes and only keeps its best attempt — cutting its
 * iterations from 40 to 18 was enough to bring back 34 overflows. Here the result does not
 * depend on convergence: each direction is solved exactly, up to the bisection step.
 */
function resous(epreuves: Epreuve[]): { x: number; y: number } {
  if (!epreuves.length) return { x: 0, y: 0 }

  /** The tightest margin across all trials, for a given translation. */
  const marge = (tx: number, ty: number) => {
    let m = Infinity
    for (const ep of epreuves) m = Math.min(m, pire(ep.contour, ep.empreintes, tx, ty).marge)
    return m
  }

  // Required margin: the tightest the original profile tolerates, across all trials.
  // Then capped by the most clearance the shape can offer the pair at its centre.
  let requis = Infinity
  for (const ep of epreuves) {
    requis = Math.min(requis, pire(ep.calContour, ep.reference, 0, 0).marge)
  }
  /*
   * Travel must be able to reach the body centre: `wide` has 87-unit-long capsules, and
   * on a triangle they only fit near the middle, about fifty units from their nominal
   * position. A fixed travel left them outside.
   */
  let mx = 0
  let my = 0
  const emps = epreuves[0]!.empreintes
  for (const e of emps) {
    mx -= e.x / emps.length
    my -= e.y / emps.length
  }
  const course = Math.max(0.35 * R, Math.hypot(mx, my) * 1.25)

  // Ceiling on the requirement: what the shape offers at its centre, always reachable.
  requis = Math.min(requis, marge(mx, my))

  /*
   * Already fine: the circle case, and any shape wide enough. The capsule must FIT INSIDE
   * as well as not being tighter than on the original profile — without that second
   * condition, a shape where nothing fits satisfies the first degenerately and we gave
   * up. `wide` has 87-unit-long capsules, `notify` a 50-unit diameter: on a triangle or
   * teardrop they overflow no matter what, and we must then aim for the least bad, not
   * give up.
   */
  const depart = marge(0, 0)
  if (depart >= requis && depart >= 0) return { x: 0, y: 0 }
  const cible = Math.max(requis, 0)

  let meilleurX = 0
  let meilleurY = 0
  let meilleureNorme = Infinity
  // fallback when nothing fits: the translation that clears the most, probed along the way
  let secoursX = 0
  let secoursY = 0
  let secours = depart

  for (let d = 0; d < DIRECTIONS; d++) {
    const a = (d / DIRECTIONS) * Math.PI * 2
    const ux = Math.cos(a)
    const uy = Math.sin(a)
    if (marge(ux * course, uy * course) < cible) {
      // this direction leads nowhere; still keep the best clearance
      // no solution that way, but perhaps a better clearance
      for (const k of [0.3, 0.6, 1]) {
        const m = marge(ux * course * k, uy * course * k)
        if (m > secours) {
          secours = m
          secoursX = ux * course * k
          secoursY = uy * course * k
        }
      }
      continue
    }
    // the shortest distance that fits, along this direction
    let bas = 0
    let haut = course
    for (let i = 0; i < DICHOTOMIE; i++) {
      const mid = (bas + haut) / 2
      if (marge(ux * mid, uy * mid) >= cible) haut = mid
      else bas = mid
    }
    if (haut < meilleureNorme) {
      meilleureNorme = haut
      meilleurX = ux * haut
      meilleurY = uy * haut
    }
  }

  const x = meilleureNorme === Infinity ? secoursX : meilleurX
  const y = meilleureNorme === Infinity ? secoursY : meilleurY
  // rendered in SPHERE-RADIUS units: the engine rescales it
  return { x: +(x / R).toFixed(6), y: +(y / R).toFixed(6) }
}

/**
 * The face to cover: the expression's if the state accepts it, its own otherwise.
 *
 * ONE table entry per expression, not a worst case common to all. A worst case seemed
 * safer — a constant offset cannot move when the expression changes — but it is
 * untenable: on a capsule, `neutre` has high eyes and asks to move down when `effraye`
 * has low eyes and asks to move up. No single translation satisfies both, and measurement
 * confirms it (4 overflows of 4.8 units).
 *
 * One entry per expression is no less smooth: the engine interpolates between TWO
 * CONSTANTS, which is monotone by construction. What trembled was re-solving the problem
 * on an interpolating gaze.
 */
function visageDe(def: StateDef, pose: Pose, expr: BotExpression | null): Visage {
  if (def.baseFace && expr) return { gaze: expr.gaze, split: expr.split, eyes: expr.eyes }
  return { gaze: pose.gaze, split: pose.split, eyes: pose.eyes }
}

/** Timestamps to sample in a state: one if its pose does not move. */
function dates(def: StateDef): number[] {
  /** Everything the solver uses: if nothing moves, one timestamp is enough. */
  const signature = (p: Pose) =>
    JSON.stringify([p.gaze, p.split, p.eyes, p.sil.rot, p.sil.cx, p.sil.cy, p.sil.sx, p.sil.sy])
  if (signature(def.pose(0)) === signature(def.pose(def.duration))) return [0]
  const n = def.id === 'wink' ? 5 : 3
  return Array.from({ length: n }, (_, i) => (i / (n - 1)) * def.duration)
}

/** The offset for a shape on a state and expression, drift included. */
function decalagePour(
  def: StateDef,
  radii: number[],
  expr: BotExpression | null
): { x: number; y: number } {
  const epreuves: Epreuve[] = []
  for (const t of dates(def)) {
    const pose = def.pose(t)
    const contour = toPoints({ ...pose.sil, radii }, R)
    const calContour = toPoints(pose.sil, R)
    const v = visageDe(def, pose, expr)
    const catalogue = isConcavePocketState(def) && !def.id.startsWith('agent')
    const gazeBias = pocketGazeBias(
      radii,
      v.gaze,
      def.baseFace ? expr : null,
      catalogue,
      def.id
    )
    const roll = catalogue ? animationPocketRoll(v.gaze.roll, radii, def.id) : v.gaze.roll
    // The four drift corners bound the nominal pose, which is their centre: testing it
    // as well would change no margin and costs one trial in five.
    const coins: Visage[] = []
    for (const dy of [-DERIVE_YAW, DERIVE_YAW]) {
      for (const dp of [-DERIVE_PITCH, DERIVE_PITCH]) {
        coins.push({
          ...v,
          gaze: gazeWithBias(
            { yaw: v.gaze.yaw + dy, pitch: v.gaze.pitch + dp, roll },
            gazeBias
          )
        })
      }
    }
    for (const c of coins) {
      epreuves.push({
        empreintes: empreintes(def, c, pose.sil, radii),
        reference: empreintes(def, c, pose.sil, pose.sil.radii),
        contour,
        calContour
      })
    }
  }
  return resous(epreuves)
}

/** Zero, the common value for everything that needs no correction. */
const NUL = { x: 0, y: 0 } as const

/** Key for an entry: the state, and the expression when the state accepts it. */
const clef = (state: StateId, expr: string | null) => `${state}|${expr ?? ''}`

/** Agent loops share the idle pocket offset — per-state tables fight animated gaze. */
function eyefitState(state: StateId): StateId {
  if (state.startsWith('agent') && state !== 'agentIdle') return 'agentIdle'
  return state
}

/**
 * Offset table, built at import: one entry per (shape, base-body state, expression). Only
 * `idle` and `swirl` carry the resting face, so only they vary by expression — the
 * other three base-body states have a face measured from the video and a single entry.
 *
 * Keyed by radii-array REFERENCE, which is already the engine convention: its guards
 * `radii === this.shape` and `expression === this.expr` rely on the same stability. An
 * unknown profile, or `null`, corrects nothing — the API accepts any array and the engine
 * does not have to depend on callers being careful.
 */
function batir(): Map<number[], Map<string, { x: number; y: number }>> {
  return new Map(
  SHAPES.map((forme) => {
    // Cluster marks are multi-body UI composites — never a single silhouette.
    if (forme.cluster) return null
    const par = new Map<string, { x: number; y: number }>()
    for (const def of STATES) {
      // Animated agent loops share agentIdle eyefit — one pocket, not per-sweep offsets.
      if (!def.baseBody) continue
      if (def.id.startsWith('agent') && def.id !== 'agentIdle') continue
      const expressions = def.baseFace ? [null, ...EXPRESSIONS] : [null]
      for (const expr of expressions) {
        par.set(clef(def.id, expr?.id ?? null), decalagePour(def, forme.radii, expr))
      }
    }
    return [forme.radii, par] as const
  }).filter((e): e is readonly [number[], Map<string, { x: number; y: number }>] => e !== null)
  )
}

const DECALAGES = batir()

/**
 * Offset to apply to both eyes for this shape in this state, in sphere-radius units —
 * the engine rescales it.
 *
 * Zero whenever the shape is not in the catalogue, which covers `null` and the circle: on
 * the circle both profiles are the same, so the margin is already what is required and
 * descent exits on the first pass. The shape measured from the video therefore does not
 * move, with no special case.
 */
export function decalageDesYeux(
  radii: number[] | null,
  state: StateId,
  expr: string | null
): { x: number; y: number } {
  if (!radii) return NUL
  const par = DECALAGES.get(radii)
  if (!par) return NUL
  const key = eyefitState(state)
  return par.get(clef(key, expr)) ?? par.get(clef(key, null)) ?? NUL
}

/** For tests: what is needed to verify the table without redoing the geometry. */
/** For tests: what is needed to time table construction. */
export const POUR_TESTS = { batir }
